import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Modal, ModalHeader, Ic, inputStyle } from './ui';
import { formatBRL, FILTER_KEY_MAP } from '../data/constants';
import { login, register, forgotPassword } from '../services/auth.js';
import { apiEnabled, tokens } from '../services/api.js';
import { startCheckout } from '../services/payments.js';
import { ModalErrorBoundary } from './ErrorBoundary';

// ─── Settings ───
export function SettingsModal({ onClose }) {
  const { dark, toggle } = useTheme();
  return (
    <Modal onClose={onClose} maxWidth={400}>
      <ModalHeader title="Configurações" onClose={onClose} icon={<Ic name="sun" s={20} c="var(--br)" />} />
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--s2)', borderRadius: 12, cursor: 'pointer', border: '1px solid var(--bd)', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Ic name={dark ? 'moon' : 'sun'} s={20} c="var(--br)" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx)' }}>Tema {dark ? 'Escuro' : 'Claro'}</div>
            <div style={{ fontSize: 12, color: 'var(--t3)' }}>Clique para alternar</div>
          </div>
        </div>
        <div style={{ width: 40, height: 22, borderRadius: 11, background: dark ? 'var(--ac)' : 'var(--s3)', position: 'relative', transition: '.3s' }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: dark ? 20 : 2, transition: '.3s' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div onClick={() => { if (dark) toggle(); }} style={{ flex: 1, padding: 14, borderRadius: 12, cursor: 'pointer', background: '#f7f8fa', border: '2px solid ' + (dark ? '#dfe4ea' : '#ea580c'), textAlign: 'center' }}>
          <Ic name="sun" s={18} c="#ea580c" /><div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginTop: 4 }}>Claro</div>
        </div>
        <div onClick={() => { if (!dark) toggle(); }} style={{ flex: 1, padding: 14, borderRadius: 12, cursor: 'pointer', background: '#0c1017', border: '2px solid ' + (dark ? '#f59e0b' : '#1e2d45'), textAlign: 'center' }}>
          <Ic name="moon" s={18} c="#f59e0b" /><div style={{ fontSize: 12, fontWeight: 600, color: '#edf2f7', marginTop: 4 }}>Escuro</div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Paywall ───
const ERROR_MSGS = {
  invalid_credentials: 'E-mail ou senha incorretos.',
  email_in_use: 'Este e-mail já está cadastrado.',
  session_expired: 'Sessão expirada. Faça login novamente.',
};

export function PaywallModal({ onClose, onLogin, initialTab = 'plans' }) {
  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('pro_annual');
  // When true, the next successful login/register triggers Stripe checkout
  // instead of just closing the modal.
  const [pendingCheckout, setPendingCheckout] = useState(false);
  // Forgot-password sub-flow. `forgotSent` flips on after the request lands;
  // we always show the same neutral confirmation so the modal can't be used
  // to enumerate registered emails.
  const [forgotSent, setForgotSent] = useState(false);

  const clearError = () => { setError(''); setForgotSent(false); };

  async function goCheckout() {
    setLoading(true); setError('');
    try {
      await startCheckout(selectedPlan); // navigates away on success
    } catch (e) {
      if (e.message === 'already_subscribed') {
        setError('Você já tem uma assinatura ativa.');
      } else {
        setError('Não foi possível abrir o checkout. Tente novamente.');
      }
      setLoading(false);
    }
  }

  function handleAssinar() {
    clearError();
    if (tokens.access) {
      // Already authed — go straight to Stripe
      goCheckout();
    } else {
      // Need to register (or login) first, then continue to checkout
      setPendingCheckout(true);
      setTab('reg');
    }
  }

  async function handleLogin() {
    if (!email || !pass) return;
    if (!apiEnabled) { onLogin({ email, name: email.split('@')[0] }); return; }
    setLoading(true); setError('');
    try {
      const user = await login(email, pass);
      onLogin(user);
      if (pendingCheckout) await goCheckout();
    } catch (e) {
      setError(ERROR_MSGS[e.message] || 'Erro ao entrar. Tente novamente.');
    } finally { setLoading(false); }
  }

  async function handleForgot() {
    if (!email) return;
    setLoading(true); setError('');
    try { await forgotPassword(email); }
    catch { /* swallow — generic confirmation either way */ }
    setForgotSent(true);
    setLoading(false);
  }

  async function handleRegister() {
    if (!name || !email || !pass) return;
    if (pass.length < 8) { setError('Senha deve ter no mínimo 8 caracteres.'); return; }
    if (!apiEnabled) { onLogin({ email, name }); return; }
    setLoading(true); setError('');
    try {
      const user = await register(email, pass, name);
      onLogin(user);
      if (pendingCheckout) await goCheckout();
    } catch (e) {
      setError(ERROR_MSGS[e.message] || 'Erro ao cadastrar. Tente novamente.');
    } finally { setLoading(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ov)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 10 }} onClick={onClose}>
      <div className="modal-inner" onClick={(e) => e.stopPropagation()} style={{ background: 'var(--sf)', borderRadius: 20, maxWidth: 460, width: '100%', overflow: 'hidden', border: '1px solid var(--bd)', animation: 'slideUp .4s ease', padding: 0 }}>
        <div style={{ background: 'linear-gradient(135deg,#ea580c,#f59e0b)', padding: '24px 20px', color: '#fff', textAlign: 'center' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>Acesso Completo — LupaLeilão</h2>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--bd)' }}>
          {[['plans', 'Planos'], ['login', 'Entrar'], ['reg', 'Cadastrar']].map(([k, l]) => (
            <button key={k} onClick={() => { setTab(k); clearError(); }} style={{ flex: 1, padding: 10, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: tab === k ? 'var(--br)' : 'var(--t3)', borderBottom: tab === k ? '2px solid var(--br)' : '2px solid transparent' }}>{l}</button>
          ))}
        </div>
        <div style={{ padding: '18px 20px 22px' }}>
          {tab === 'plans' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { key: 'pro_monthly', label: 'Mensal',  price: 'R$49,90/mês', priceNote: null,                          badge: null },
                { key: 'pro_annual',  label: 'Anual',   price: 'R$39,90/mês', priceNote: 'Cobrado R$478,80 uma vez/ano', badge: 'Economize 20%' },
              ].map((opt) => {
                const active = selectedPlan === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSelectedPlan(opt.key)}
                    aria-pressed={active}
                    style={{
                      border: active ? '2px solid var(--br)' : '1px solid var(--bd)',
                      borderRadius: 12, padding: 14, textAlign: 'left',
                      background: active ? 'var(--bb)' : 'var(--sf)',
                      color: 'var(--tx)', cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                      transition: 'border-color .15s, background .15s',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid ' + (active ? 'var(--br)' : 'var(--bd)'), display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {active && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--br)' }} />}
                      </span>
                      <span style={{ fontWeight: 600 }}>{opt.label}</span>
                      {opt.badge && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gn)', background: 'var(--gl)', padding: '2px 6px', borderRadius: 4 }}>{opt.badge}</span>}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--br)', lineHeight: 1 }}>{opt.price}</span>
                      {opt.priceNote && (
                        <span style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 500 }}>{opt.priceNote}</span>
                      )}
                    </span>
                  </button>
                );
              })}
              {error && <p style={{ fontSize: 12, color: 'var(--rd)', margin: 0 }}>{error}</p>}
              <button
                onClick={handleAssinar}
                disabled={loading}
                style={{ padding: 12, borderRadius: 10, border: 'none', background: loading ? 'var(--s3)' : 'var(--br)', color: loading ? 'var(--t3)' : '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'default' : 'pointer' }}
              >
                {loading ? 'Abrindo checkout…' : 'Assinar'}
              </button>
            </div>
          )}
          {tab === 'login' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" style={inputStyle} disabled={loading} />
              <input value={pass} onChange={(e) => setPass(e.target.value)} type="password" placeholder="Senha"
                style={inputStyle} disabled={loading}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }} />
              {error && <p style={{ fontSize: 12, color: 'var(--rd)', margin: 0 }}>{error}</p>}
              <button onClick={handleLogin} disabled={loading || !email || !pass}
                style={{ padding: 12, borderRadius: 10, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
              <button type="button" onClick={() => { setTab('forgot'); clearError(); }}
                style={{ background: 'none', border: 'none', color: 'var(--t3)', fontSize: 12, cursor: 'pointer', padding: 4, fontFamily: 'inherit', alignSelf: 'center' }}>
                Esqueci minha senha
              </button>
            </div>
          )}
          {tab === 'forgot' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!forgotSent ? (
                <>
                  <p style={{ fontSize: 13, color: 'var(--t2)', margin: 0, lineHeight: 1.5 }}>
                    Informe seu e-mail. Se houver uma conta cadastrada, enviaremos um link para redefinir sua senha.
                  </p>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" style={inputStyle} disabled={loading}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleForgot(); }} />
                  <button onClick={handleForgot} disabled={loading || !email}
                    style={{ padding: 12, borderRadius: 10, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Enviando...' : 'Enviar link'}
                  </button>
                </>
              ) : (
                <div style={{ background: 'var(--gl)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                  <p style={{ fontSize: 13, color: 'var(--tx)', margin: 0, lineHeight: 1.5 }}>
                    Se este e-mail estiver cadastrado, você receberá um link para redefinir sua senha em alguns minutos. O link é válido por 60 minutos.
                  </p>
                </div>
              )}
              <button type="button" onClick={() => { setTab('login'); clearError(); }}
                style={{ background: 'none', border: 'none', color: 'var(--t3)', fontSize: 12, cursor: 'pointer', padding: 4, fontFamily: 'inherit', alignSelf: 'center' }}>
                ← Voltar para entrar
              </button>
            </div>
          )}
          {tab === 'reg' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" style={inputStyle} disabled={loading} />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" style={inputStyle} disabled={loading} />
              <input value={pass} onChange={(e) => setPass(e.target.value)} type="password" placeholder="Senha (mín. 8 caracteres)"
                style={inputStyle} disabled={loading}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRegister(); }} />
              {error && <p style={{ fontSize: 12, color: 'var(--rd)', margin: 0 }}>{error}</p>}
              <button onClick={handleRegister} disabled={loading || !name || !email || !pass}
                style={{ padding: 12, borderRadius: 10, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Criando conta...' : 'Criar Conta'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calculator ───
export function CalculatorModal({ property, onClose }) {
  const pr = property || { valorMinimo: 200000, valorAvaliacao: 400000 };
  const [vc, setVc] = useState(pr.valorMinimo);
  const [vr, setVr] = useState(0);
  const [vv, setVv] = useState(Math.floor(pr.valorAvaliacao * 1.1));
  const [tx, setTx] = useState(5);
  const [it, setIt] = useState(3);
  const [rg, setRg] = useState(3000);
  const [hn, setHn] = useState(5000);
  const [ds, setDs] = useState(0);

  const total = vc + vr + (vc * tx / 100) + (vc * it / 100) + rg + hn + ds;
  const lucro = vv - total;
  const roi = total > 0 ? (lucro / total) * 100 : 0;
  const ok = roi > 15;

  const fields = [['Compra', vc, setVc], ['Reforma', vr, setVr], ['Venda', vv, setVv], ['Taxas%', tx, setTx], ['ITBI%', it, setIt], ['Cartório', rg, setRg], ['Advogado', hn, setHn], ['Desocup.', ds, setDs]];
  const results = [['Custo', formatBRL(total), 'var(--tx)'], ['Lucro', formatBRL(lucro), lucro > 0 ? 'var(--gn)' : 'var(--rd)'], ['ROI', roi.toFixed(1) + '%', roi > 15 ? 'var(--gn)' : 'var(--rd)']];

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Calculadora de Viabilidade" onClose={onClose} icon={<Ic name="calc" s={18} c="var(--br)" />} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {fields.map(([label, value, setter]) => (
          <div key={label}>
            <label style={{ fontSize: 11, color: 'var(--t3)', display: 'block', marginBottom: 2 }}>{label}</label>
            <input type="number" value={value} onChange={(e) => setter(+e.target.value)} style={inputStyle} />
          </div>
        ))}
      </div>
      <div style={{ background: ok ? 'var(--gl)' : 'var(--rl)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: ok ? 'var(--gn)' : 'var(--rd)', marginBottom: 10 }}>{ok ? '✓ VIÁVEL' : '✗ INVIÁVEL'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {results.map(([label, value, color]) => (
            <div key={label}>
              <div style={{ fontSize: 10, color: 'var(--t3)' }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── AI Analysis (Groq/Gemini free tier + fallback simulado) ───
import { analyzePropertyRisk, llmConfigured } from '../services/llmService.js';

const rand = (a, b) => Math.floor(Math.random() * (b - a)) + a;

function generateSimulatedResult() {
  const score = rand(30, 70);
  return {
    locationAdvantages: ['Região com infraestrutura urbana', 'Acesso a transporte público'],
    riskScore: score,
    riskLevel: score < 40 ? 'baixo' : score < 65 ? 'moderado' : 'elevado',
    riskFactors: score > 50 ? ['Verificar pendências judiciais', 'Checar débitos condominiais'] : ['Nenhum fator crítico identificado'],
    matriculaItems: [
      { name: 'Cadeia Dominial', ok: true, detail: 'OK' },
      { name: 'Ônus e Gravames', ok: Math.random() > 0.3, detail: 'Verificado' },
      { name: 'Débitos Condominiais', ok: Math.random() > 0.5, detail: 'Analisado' },
      { name: 'Ações Judiciais', ok: Math.random() > 0.4, detail: 'Verificado' },
      { name: 'Regularidade Edital', ok: true, detail: 'Conforme' },
      { name: 'Notificação Devedor', ok: Math.random() > 0.3, detail: 'Notificado' },
      { name: 'Averbação', ok: Math.random() > 0.4, detail: 'Construção' },
    ],
    recommendation: score < 50 ? 'Risco moderado. Análise jurídica recomendada.' : 'Risco elevado. Consulte advogado especializado.',
  };
}

function AIAnalysisContent({ property, onClose }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [simulated, setSimulated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (llmConfigured) {
        try {
          const res = await analyzePropertyRisk(property);
          if (!cancelled) { setResult(res); setSimulated(false); setLoading(false); }
          return;
        } catch (err) {
          console.warn('[AI] LLM call failed:', err.message);
        }
      }
      // Fallback: resultado simulado (claramente marcado)
      if (!cancelled) {
        setResult(generateSimulatedResult());
        setSimulated(true);
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [property.id]);

  const inner = (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--bd)', borderTopColor: 'var(--br)', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin .8s linear infinite' }} />
          <p style={{ color: 'var(--t2)' }}>Analisando com IA...</p>
        </div>
      ) : result && (
        <div>
          {result.locationAdvantages?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 6 }}>Vantagens da Localização</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {result.locationAdvantages.map((adv, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 10px', background: 'var(--gl)', borderRadius: 7, fontSize: 12 }}>
                    <Ic name="check" s={12} c="var(--gn)" />
                    <span style={{ color: 'var(--t2)' }}>{adv}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ background: 'var(--s2)', borderRadius: 12, padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'conic-gradient(' + (result.riskScore < 50 ? 'var(--gn)' : 'var(--rd)') + ' ' + result.riskScore + '%, var(--s3) 0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--sf)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: 'var(--tx)' }}>{result.riskScore}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>Risco {result.riskLevel}</div>
              {result.riskFactors?.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>{result.riskFactors.join(' · ')}</div>
              )}
            </div>
          </div>
          {result.matriculaItems?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
              {result.matriculaItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--s2)', borderRadius: 8, borderLeft: '3px solid ' + (item.ok ? 'var(--gn)' : 'var(--rd)') }}>
                  <Ic name={item.ok ? 'check' : 'x'} s={13} c={item.ok ? 'var(--gn)' : 'var(--rd)'} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>{item.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--t3)', marginLeft: 'auto' }}>{item.detail}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: 'var(--ol)', borderRadius: 10, padding: 12, fontSize: 13, color: 'var(--t2)' }}>
            {result.recommendation}
          </div>
          {simulated && (
            <div style={{ fontSize: 10, color: 'var(--t3)', fontStyle: 'italic', marginTop: 8, textAlign: 'center', padding: '6px 10px', background: 'var(--s2)', borderRadius: 6 }}>
              ⚠️ Análise SIMULADA — configure VITE_GEMINI_API_KEY, VITE_CEREBRAS_API_KEY ou VITE_MISTRAL_API_KEY para análise real por IA. Não use para tomada de decisão.
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <Modal onClose={onClose} maxWidth={500}>
      <ModalHeader title={'Análise IA — nº ' + (property.matricula ?? property.id)} onClose={onClose} icon={<Ic name="ai" s={18} c="var(--br)" />} />
      {inner}
    </Modal>
  );
}

export function AIAnalysisModal({ property, onClose }) {
  return (
    <ModalErrorBoundary>
      <AIAnalysisContent property={property} onClose={onClose} />
    </ModalErrorBoundary>
  );
}

// ─── Profile / Account ───
export function ProfileModal({ user, onClose, onLogout, onUpdate, savedFilters, onDeleteFilter, onUpgrade, onApplyFilter }) {
  const [tab, setTab] = useState('profile');
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [saved, setSaved] = useState(false);

  return (
    <Modal onClose={onClose} maxWidth={480}>
      <ModalHeader title="Minha Conta" onClose={onClose} icon={<Ic name="user" s={18} c="var(--br)" />} />
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--bd)', marginBottom: 16 }}>
        {[['profile', 'Perfil'], ['filters', 'Meus Alertas'], ['plan', 'Plano']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ flex: 1, padding: '8px 4px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: tab === k ? 'var(--br)' : 'var(--t3)', borderBottom: tab === k ? '2px solid var(--br)' : '2px solid transparent' }}>{l}</button>
        ))}
      </div>

      {tab === 'profile' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--bb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: 'var(--br)' }}>
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx)' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'var(--t3)' }}>{user.email}</div>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--t3)', display: 'block', marginBottom: 2 }}>Nome</label>
            <input value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--t3)', display: 'block', marginBottom: 2 }}>E-mail</label>
            <input value={email} onChange={(e) => { setEmail(e.target.value); setSaved(false); }} style={inputStyle} />
          </div>
          <button onClick={() => { onUpdate({ ...user, name, email }); setSaved(true); }}
            style={{ padding: 10, borderRadius: 9, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            {saved ? 'Salvo!' : 'Salvar Alterações'}
          </button>
          <button onClick={onLogout}
            style={{ padding: 10, borderRadius: 9, border: '1px solid var(--rd)', background: 'transparent', color: 'var(--rd)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Sair da Conta
          </button>
        </div>
      )}

      {tab === 'filters' && (
        <div>
          {(!savedFilters || savedFilters.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Ic name="bell" s={28} c="var(--t3)" />
              <p style={{ fontSize: 13, color: 'var(--t3)', marginTop: 8 }}>Nenhum alerta salvo.</p>
              <p style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>Use "Salvar/Alerta" nos filtros para criar alertas.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {savedFilters.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--s2)', borderRadius: 10, border: '1px solid var(--bd)', transition: 'background .15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--s3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--s2)'; }}>
                  <div
                    onClick={() => onApplyFilter?.(f)}
                    style={{ flex: 1, cursor: onApplyFilter ? 'pointer' : 'default' }}
                    role={onApplyFilter ? 'button' : undefined}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                      {(f.alertEnabled ?? f.useEmail) ? 'E-mail' : 'Sem alertas'}
                      {f.filters?.uf && (' · ' + f.filters.uf)}
                      {f.filters?.tipo && (' · ' + f.filters.tipo)}
                      {f.filters?.banco && (' · ' + f.filters.banco)}
                    </div>
                  </div>
                  <button onClick={() => onDeleteFilter(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <Ic name="x" s={14} c="var(--rd)" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'plan' && (
        (user?.plan === 'pro' || user?.plan === 'pro_annual') ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'var(--gl)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--gn)', fontWeight: 600, marginBottom: 4 }}>PLANO ATIVO</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--gn)' }}>Premium</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>Acesso ilimitado a buscas, alertas e análise IA</div>
            </div>
            <div style={{ border: '1px solid var(--bd)', borderRadius: 10, padding: 12, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--t3)' }}>Plano</span>
              <span style={{ fontWeight: 600, color: 'var(--tx)' }}>{user.plan === 'pro_annual' ? 'Anual' : 'Mensal'}</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'var(--s2)', borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid var(--bd)' }}>
              <div style={{ fontSize: 12, color: 'var(--t3)', fontWeight: 600, marginBottom: 4 }}>PLANO ATIVO</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--tx)' }}>Gratuito</div>
              <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>3 buscas por dia · sem alertas · sem análise IA</div>
            </div>
            <div style={{ border: '1px solid var(--br)', borderRadius: 12, padding: 14, background: 'var(--bb)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--br)', marginBottom: 6 }}>Upgrade para Premium</div>
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 12, color: 'var(--t2)', lineHeight: 1.6 }}>
                <li>Buscas ilimitadas</li>
                <li>Alertas personalizados por e-mail e WhatsApp</li>
                <li>Análise de risco por IA</li>
              </ul>
              <button
                type="button"
                onClick={() => { onClose(); onUpgrade?.(); }}
                style={{ width: '100%', padding: 11, borderRadius: 10, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                Assinar Premium →
              </button>
            </div>
          </div>
        )
      )}
    </Modal>
  );
}

// ─── Save Filter ───
// WhatsApp channel is deferred (pending Meta Cloud API integration). Until
// it ships, the modal only offers e-mail alerts pinned to the account email
// — removes a manual input that was already a duplicate of the signup data.
//
// The App state uses short filter keys (uf/ci/tp/bk/md/pm/px/qm) for URL
// compactness, but the backend saved_filters schema expects the long-form
// keys. Without translating, the backend's `.strict()` validator silently
// rejected the payload and nothing was persisted. Translate once here.
const NUMERIC_FILTER_KEYS = new Set(['precoMin', 'precoMax', 'quartos']);
function toApiFilters(shortForm) {
  const out = {};
  for (const [k, v] of Object.entries(shortForm ?? {})) {
    const apiKey = FILTER_KEY_MAP[k];
    if (!apiKey || v == null || v === '') continue;
    out[apiKey] = NUMERIC_FILTER_KEYS.has(apiKey) ? Number(v) : v;
  }
  return out;
}

export function SaveFilterModal({ filters, user, onClose, onSave }) {
  const [name, setName] = useState('');
  const [useEmail, setUseEmail] = useState(true);
  const email = user?.email ?? '';

  const Toggle = ({ on, onToggle, label, color }) => (
    <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: on ? (color === 'var(--gn)' ? 'var(--gl)' : 'var(--al)') : 'var(--s2)', borderRadius: 8, cursor: 'pointer', border: '1px solid var(--bd)' }}>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--tx)' }}>{label}</span>
      <div style={{ width: 30, height: 16, borderRadius: 8, background: on ? color : 'var(--s3)', position: 'relative' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: on ? 16 : 2, transition: '.2s' }} />
      </div>
    </div>
  );

  return (
    <Modal onClose={onClose} maxWidth={420}>
      <ModalHeader title="Salvar Filtro & Alertas" onClose={onClose} icon={<Ic name="bell" s={18} c="var(--br)" />} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do filtro" style={inputStyle} />
        <Toggle on={useEmail} onToggle={() => setUseEmail(!useEmail)} label="Alerta por e-mail" color="var(--ac)" />
        {useEmail && (
          <div style={{ ...inputStyle, background: 'var(--s2)', color: 'var(--t2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Ic name="mail" s={13} c="var(--t3)" />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{email || 'E-mail não disponível'}</span>
          </div>
        )}
        <button onClick={() => { if (name) { onSave({
            name,
            filters: toApiFilters(filters),
            alertEnabled: useEmail && !!email,
            alertChannel: 'email',
            alertEmail: useEmail ? email : undefined,
          }); onClose(); } }} disabled={!name}
          style={{ padding: 11, borderRadius: 10, border: 'none', background: name ? 'var(--br)' : 'var(--s3)', color: name ? '#fff' : 'var(--t3)', fontWeight: 600, cursor: name ? 'pointer' : 'not-allowed' }}>
          Salvar
        </button>
      </div>
    </Modal>
  );
}

// ─── Contact ───
const API_URL = import.meta.env.VITE_API_URL || '';

export function ContactModal({ onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  const canSubmit = name.trim().length > 0 && emailOk && message.trim().length >= 10;

  async function submit() {
    if (!canSubmit || status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message: message.trim(), website }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 429) throw new Error('Você atingiu o limite de envios. Tente novamente mais tarde.');
        if (body?.error === 'invalid_email') throw new Error('E-mail inválido.');
        if (body?.error === 'invalid_message') throw new Error('Mensagem muito curta (mínimo 10 caracteres).');
        throw new Error('Não foi possível enviar sua mensagem. Tente novamente.');
      }
      setStatus('sent');
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao enviar.');
      setStatus('error');
    }
  }

  return (
    <Modal onClose={onClose} maxWidth={440}>
      <ModalHeader title="Fale conosco" onClose={onClose} icon={<Ic name="mail" s={18} c="var(--br)" />} />
      {status === 'sent' ? (
        <div style={{ textAlign: 'center', padding: '16px 8px' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>✓</div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx)', marginBottom: 6 }}>Mensagem enviada</h3>
          <p style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 18 }}>Retornaremos pelo e-mail informado em breve.</p>
          <button onClick={onClose} style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: 'var(--br)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            Fechar
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Seu nome" maxLength={100} style={inputStyle}
          />
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com" maxLength={200} style={inputStyle}
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Como podemos ajudar?" rows={5} maxLength={4000}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: 100 }}
          />
          {/* Honeypot — hidden from users, bots will fill it */}
          <input
            type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)}
            aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          />
          {errorMsg && (
            <div style={{ padding: 10, background: 'var(--rl)', color: 'var(--rd)', borderRadius: 8, fontSize: 12 }}>
              {errorMsg}
            </div>
          )}
          <button
            onClick={submit} disabled={!canSubmit || status === 'sending'}
            style={{
              padding: 11, borderRadius: 10, border: 'none',
              background: (canSubmit && status !== 'sending') ? 'var(--br)' : 'var(--s3)',
              color: (canSubmit && status !== 'sending') ? '#fff' : 'var(--t3)',
              fontWeight: 600,
              cursor: (canSubmit && status !== 'sending') ? 'pointer' : 'not-allowed',
            }}
          >
            {status === 'sending' ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      )}
    </Modal>
  );
}
